import { useStoryblokApi } from "gatsby-source-storyblok"

import {
  ConsolidatedItemEntry,
  useConsolidatedItemEntries,
} from "./useConsolidatedItemNodes"

import { useGetBundleByCode } from "@hooks/bundles"
import { useGetProductBySku } from "@hooks/products"
import {
  consolidateBundle,
  consolidateOnlineProduct,
  consolidateProduct,
  STORYBLOK_ITEM_PATHS,
} from "@utils/V2/consolidateItem"
import { isStoryblokPreview } from "@utils/V2/storyblokPreview"
import { useCallback, useEffect, useMemo, useState } from "react"

const DRAFT_PAGE_SIZE = 100

export type ShopItems = {
  heroItems: YocoCom.ConsolidatedItem[]
  catalogueItems: YocoCom.ConsolidatedItem[]
}

const INTERNAL_ITEM_PATH = /^settings\/[^/]+\/internal\//

const ITEM_TYPE_ORDER = ["product", "bundle", "onlineProduct"]

type ItemStory = {
  uuid: string
  full_slug: string
  content:
    | Storyblok.ProductConfig
    | Storyblok.BundleConfig
    | Storyblok.OnlineProductConfig
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null

const isItemStory = (value: unknown): value is ItemStory => {
  if (!isRecord(value)) return false

  const content = value.content

  if (!isRecord(content)) return false

  return (
    typeof value.uuid === "string" &&
    typeof value.full_slug === "string" &&
    (content.component === "product" ||
      content.component === "bundle" ||
      content.component === "onlineProduct")
  )
}

const compareByPriority =
  (getPriority: (item: YocoCom.ConsolidatedItem) => number | undefined) =>
  (
    firstItem: YocoCom.ConsolidatedItem,
    secondItem: YocoCom.ConsolidatedItem
  ) => {
    const firstPriority = getPriority(firstItem)
    const secondPriority = getPriority(secondItem)

    if (firstPriority !== secondPriority) {
      if (firstPriority === undefined) return 1
      if (secondPriority === undefined) return -1

      return firstPriority - secondPriority
    }

    return firstItem.storyblokUuid.localeCompare(secondItem.storyblokUuid)
  }

const internalOrder = (
  firstEntry: ConsolidatedItemEntry,
  secondEntry: ConsolidatedItemEntry
) => {
  const rank = (entry: ConsolidatedItemEntry) => {
    const index = ITEM_TYPE_ORDER.indexOf(entry.item.type)
    return index === -1 ? ITEM_TYPE_ORDER.length : index
  }

  return (
    rank(firstEntry) - rank(secondEntry) ||
    firstEntry.fullSlug.localeCompare(secondEntry.fullSlug)
  )
}

const catalogueOrder = compareByPriority((item) => item.orderPriority)

const heroOrder = compareByPriority(
  (item) => item.heroOrderPriority ?? item.orderPriority
)

export const useShopItems = (useInternalItems = false): ShopItems => {
  const storyblokApi = useStoryblokApi()
  const nodeEntries = useConsolidatedItemEntries()
  const getSdkProduct = useGetProductBySku()
  const getSdkBundle = useGetBundleByCode()
  const [draftEntries, setDraftEntries] = useState<ConsolidatedItemEntry[]>()

  const consolidate = useCallback(
    (story: ItemStory): YocoCom.ConsolidatedItem | undefined => {
      const config = story.content
      const storyblokUuid = config.uuid || story.uuid

      if (config.component === "product") {
        const sdkProduct = getSdkProduct(config.sku, config.storefront)

        return sdkProduct
          ? consolidateProduct(config, sdkProduct, storyblokUuid)
          : undefined
      }

      if (config.component === "bundle") {
        const sdkBundle = getSdkBundle(config.code, config.storefront)

        return sdkBundle
          ? consolidateBundle(config, sdkBundle, storyblokUuid)
          : undefined
      }

      return consolidateOnlineProduct(config, storyblokUuid)
    },
    [getSdkProduct, getSdkBundle]
  )

  useEffect(() => {
    if (!storyblokApi || !isStoryblokPreview()) return

    const loadDrafts = async () => {
      const collected: ConsolidatedItemEntry[] = []

      for (const path of STORYBLOK_ITEM_PATHS) {
        const stories: unknown = await storyblokApi.getAll("cdn/stories", {
          starts_with: path,
          version: "draft",
          per_page: DRAFT_PAGE_SIZE,
        })

        for (const story of Array.isArray(stories) ? stories : []) {
          if (!isItemStory(story)) continue

          const item = consolidate(story)

          if (item) {
            collected.push({
              item,
              isInternal: INTERNAL_ITEM_PATH.test(story.full_slug),
              fullSlug: story.full_slug,
            })
          }
        }
      }

      setDraftEntries(collected)
    }

    loadDrafts().catch((error) => {
      console.error("Error loading draft shop items: ", error)
    })
  }, [storyblokApi, consolidate])

  return useMemo(() => {
    const source = draftEntries ?? nodeEntries

    if (useInternalItems) {
      const internal = source
        .filter((entry) => entry.isInternal)
        .sort(internalOrder)
        .map((entry) => entry.item)

      return { heroItems: internal, catalogueItems: internal }
    }

    const listed = source
      .map((entry) => entry.item)
      .filter((item) => item.isListed)

    return {
      heroItems: listed.filter((item) => item.showInHero).sort(heroOrder),
      catalogueItems: [...listed].sort(catalogueOrder),
    }
  }, [draftEntries, nodeEntries, useInternalItems])
}
